import { Component, inject, OnInit } from '@angular/core';
import { Auth, signInWithCustomToken, signOut } from '@angular/fire/auth';
import { Router, ActivatedRoute } from '@angular/router'; // Import Router
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AccessService } from '../services/access.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule], // Import CommonModule and FormsModule
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent implements OnInit {
  private auth = inject(Auth);
  private router = inject(Router); // Inject Router
  private route = inject(ActivatedRoute);
  private accessService = inject(AccessService);
    
  isLoading = false;
  errorMessage: string | null = null;

  showEmailPrompt = false;
  emailInput = '';
  pendingUser: any = null;

  ngOnInit() {
    // Check if we were redirected back with a token
    this.route.queryParams.subscribe(async params => {
      const token = params['token'];
      const logout = params['logout'] === 'true';
      if (token) {
        await this.handleAuthToken(token);
      } else {
        this.loginUsingAuthPortal(logout);
      }
    });
  }

  async handleAuthToken(token: string) {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      // Exchange the custom token for a Firebase User
      const result = await signInWithCustomToken(this.auth, token);
      const user = result.user;

      const idTokenResult = await user.getIdTokenResult();
      const tokenEmail = (idTokenResult.claims['email'] as string) || '';
      const email = (user.email || tokenEmail || localStorage.getItem('userEmail') || '').trim().toLowerCase();

      if (email) {
        await this.completeLogin(user, email);
      } else {
        // No email in Firebase auth user nor in custom token claims. Show popup/prompt.
        this.pendingUser = user;
        this.showEmailPrompt = true;
        this.isLoading = false;
      }
    } catch (error) {
      console.error('Login error:', error);
      this.errorMessage = 'Authentication failed. Please try again.';
      this.isLoading = false;
    }
  }

  async completeLogin(user: any, email: string) {
    this.isLoading = true;
    localStorage.setItem('userEmail', email);
    // Check/Create user profile in Firestore
    const exists = await this.accessService.saveUserProfile(user, email);
    
    if (exists) {
      // Fetch the updated profile to check the role
      const profile$ = this.accessService.getUserProfile(user.uid);
      const profile = await firstValueFrom(profile$);
      
      const roleName = profile?.role || (profile as any)?.role_id || 'user';
      if (typeof roleName === 'string' && roleName.toLowerCase() === 'user') {
        // User role is explicitly denied access to the ERP
        await signOut(this.auth);
        this.errorMessage = 'Access Denied: Your account requires administrative approval to access the system.';
        this.isLoading = false;
        return;
      }

      // SUCCESS: Redirect to Homepage
      this.router.navigate(['/home']);
    } else {
      // User not found in system
      await signOut(this.auth);
      this.errorMessage = 'Access Denied: Your account is not registered in the system.';
      this.isLoading = false;
    }
  }

  async submitEmail() {
    if (!this.emailInput || !this.emailInput.trim()) {
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.emailInput.trim())) {
      this.errorMessage = 'Please enter a valid email address format.';
      return;
    }

    if (this.pendingUser) {
      this.errorMessage = null;
      try {
        await this.completeLogin(this.pendingUser, this.emailInput.trim());
      } catch (err) {
        console.error('Error saving user profile with custom email:', err);
        this.errorMessage = 'Failed to save email. Please try again.';
      }
    }
  }

  loginUsingAuthPortal(logout = false) {
    // Redirect to external Auth Portal
    this.isLoading = true;
    this.errorMessage = null;
    
    const callbackUrl = window.location.origin + '/login';
    let portalUrl = `${environment.authPortalUrl}?redirect_url=${encodeURI(callbackUrl)}`;
    if (logout) {
      portalUrl += '&logout=true';
    }
    window.location.href = portalUrl;
  }
}
